import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import { ReportLineChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import {
  rebookRate,
  rebookRateByDay,
  rebookAttribution,
  artistQuality,
  REBOOK_BASELINE,
  REBOOK_MANAGER_TARGET,
} from '@/domain/reportingEngine';

function shortDay(dayIso: string): string {
  return new Date(`${dayIso}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

function pct(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

/** Are we compounding at 50%, and where is the shortfall - rebook rate over
 *  time, per-artist quality, per-store/manager attribution, and per-facilitator
 *  credit, all scoped to the page-wide studio + date filters. */
export default function RebooksTab({ scope }: { scope: ReportingScope }) {
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const studios = useBrainStore((s) => s.studios);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const single = singleStudioScope(studioIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rate = useMemo(() => rebookRate(days, single), [days, single, bookings]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rateByDay = useMemo(() => rebookRateByDay(days, single), [days, single, bookings]);
  // Per-store/manager attribution is always network-wide (one row per store),
  // regardless of the page-wide studio filter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const networkAttribution = useMemo(() => rebookAttribution(days), [days, bookings, artists, agents]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const staffAttribution = useMemo(() => rebookAttribution(days, single), [days, single, bookings, artists, agents]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quality = useMemo(() => artistQuality(single), [single, bookings, salePayments, pieces, artists]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => rebookRate(days, sid)) : []),
    [compareMode, studioIds, days, bookings],
  );

  if (compareMode) {
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-rebooks">
        <section className="panel p-4 overflow-x-auto">
          <h2 className="heading-serif text-lg text-cream mb-4">Rebooks, studios compared</h2>
          <table className="w-full text-sm" data-testid="rebooks-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Metric</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">
                    {studios[s.studioId]?.name ?? s.studioId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Rebook rate</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {pct(s.value.rate)}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Rebooks / completed</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {s.value.rebooks.toLocaleString('en-AU')} / {s.value.completed.toLocaleString('en-AU')}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  const chartData = rateByDay
    .filter((d) => d.cumulativePct !== null)
    .map((d) => ({ day: shortDay(d.dayIso), rate: d.cumulativePct! }));

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-rebooks">
      {/* Headline rebook rate */}
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Rebook rate" info="In-chair rebooks ÷ completed sittings this period, against the 50% studio baseline.">
          {pct(rate.rate)}
          <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-2 block">
            {rate.rebooks.toLocaleString('en-AU')} of {rate.completed.toLocaleString('en-AU')} · vs 50% baseline
          </span>
        </Tile>
      </section>

      {/* Rebook-rate line vs baselines */}
      <section className="panel p-4" data-testid="rebooks-rate-chart">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Rebook rate over time
          <InfoTip>
            Running rebook rate across the period: cumulative in-chair rebooks ÷ cumulative completed sessions, as a
            percentage. Reference lines mark the 50% studio baseline and the 40% manager target.
          </InfoTip>
        </h2>
        {chartData.length > 0 ? (
          <ReportLineChart
            data={chartData}
            xKey="day"
            hideLegend
            series={[{ key: 'rate', name: 'Cumulative rebook %', color: REPORT_COLORS.gold }]}
            references={[
              { y: REBOOK_BASELINE * 100, label: 'Baseline 50%' },
              { y: REBOOK_MANAGER_TARGET * 100, label: 'Manager 40%' },
            ]}
          />
        ) : (
          <p className="font-serif text-cream-soft text-sm">No completed sessions this period yet.</p>
        )}
      </section>

      {/* Per-artist quality */}
      <section className="panel p-4" data-testid="rebooks-by-artist">
        <h2 className="heading-serif text-lg text-cream mb-1">
          By artist
          <InfoTip>
            Per artist (all-time): average job value, repeat-booking rate (customers who came back for more than one
            session), piece-completion rate, and open sessions left (estimated minus completed across open pieces).
            Ranked by open sessions left - the artist whose booked work is dragging sits on top.
          </InfoTip>
        </h2>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Artist</th>
                <th className="text-right px-2 py-2">Avg job</th>
                <th className="text-right px-2 py-2">Repeat rate</th>
                <th className="text-right px-2 py-2">Piece completion</th>
                <th className="text-right px-2 py-2">Open sessions left</th>
              </tr>
            </thead>
            <tbody>
              {quality.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No artists in scope.
                  </td>
                </tr>
              ) : (
                quality.map((r) => (
                  <tr key={r.artistId} className="border-t border-gold-dim/15">
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                    <td className="px-2 py-2 text-right font-mono">
                      <Money amountAud={r.avgJobValueAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{pct(r.repeatRate)}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{pct(r.pieceCompletionRate)}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream">{r.openSessionsRemaining.toLocaleString('en-AU')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-store/manager attribution - always network-wide */}
      <section className="panel p-4" data-testid="rebooks-by-store">
        <h2 className="heading-serif text-lg text-cream mb-1">
          By store &amp; manager
          <InfoTip>
            Checkout outcomes per store this period: in-chair rebooked, deposit applied (declined), and sales-deferred,
            with the manager on record for that store.
          </InfoTip>
        </h2>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Store</th>
                <th className="text-left px-2 py-2">Manager</th>
                <th className="text-right px-2 py-2">Rebooked</th>
                <th className="text-right px-2 py-2">Declined</th>
                <th className="text-right px-2 py-2">Deferred</th>
                <th className="text-right px-2 py-2">Rate</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(networkAttribution.byStudio).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No completed sessions with rebook outcomes yet.
                  </td>
                </tr>
              ) : (
                Object.entries(networkAttribution.byStudio)
                  .sort(([a], [b]) => {
                    const nameA = studios[a as StudioId]?.name ?? a;
                    const nameB = studios[b as StudioId]?.name ?? b;
                    return nameA.localeCompare(nameB);
                  })
                  .map(([sid, counts]) => {
                    const studio = studios[sid as StudioId];
                    const manager = studio ? agents[studio.managerId] : undefined;
                    const managerName = manager ? `${manager.firstName} ${manager.lastName}` : (studio?.managerId ?? '—');
                    const total = counts.rebooked + counts.declined + counts.deferred;
                    const storeRate = total > 0 ? counts.rebooked / total : 0;
                    return (
                      <tr key={sid} className="border-t border-gold-dim/15">
                        <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{studio?.name ?? sid}</td>
                        <td className="px-2 py-2 font-inter text-cream-soft whitespace-nowrap">{managerName}</td>
                        <td className="px-2 py-2 text-right font-mono text-mint">{counts.rebooked}</td>
                        <td className="px-2 py-2 text-right font-mono text-blood">{counts.declined}</td>
                        <td className="px-2 py-2 text-right font-mono text-gold-bright">{counts.deferred}</td>
                        <td className="px-2 py-2 text-right font-mono text-cream">{pct(storeRate)}</td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-agent / facilitator credit */}
      <section className="panel p-4" data-testid="rebooks-by-agent">
        <h2 className="heading-serif text-lg text-cream mb-1">
          By agent
          <InfoTip>Staff ranked by how many in-chair rebooks they facilitated at checkout, in scope.</InfoTip>
        </h2>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Staff</th>
                <th className="text-left px-2 py-2">Role</th>
                <th className="text-right px-2 py-2">Rebooks</th>
              </tr>
            </thead>
            <tbody>
              {staffAttribution.staffRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No rebooks attributed yet.
                  </td>
                </tr>
              ) : (
                staffAttribution.staffRows.map((r) => (
                  <tr key={r.id} className="border-t border-gold-dim/15">
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                    <td className="px-2 py-2 font-inter text-[10px] text-cream-soft uppercase tracking-widest">{r.role}</td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.rebooks}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Tile({ label, info, children }: { label: string; info?: string; children: ReactNode }) {
  return (
    <div className="panel-dark p-4">
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
    </div>
  );
}
